import React, { ComponentType } from 'react';
import AsyncSelect from 'react-select/async';
import { Translator } from 'shared/translator/translator.component';
import {
  FindCustomersQuery,
  useFindCustomersLazyQuery,
} from './customer-finder.generated';
import styles from './customer-finder.module.scss';

function getPersonName(
  contact: FindCustomersQuery['persons']['data'][number],
  _: any,
) {
  return [
    contact.gender ? _(`gender.declarative.${contact.gender}`) : undefined,
    contact.title,
    contact.firstName,
    contact.lastName,
  ]
    .filter((t) => !!t)
    .join(' ');
}

function getPersonLabel(
  contact: FindCustomersQuery['persons']['data'][number],
  _: any,
) {
  const customerNumber = (contact.user as any)?.customerNumber;
  return [
    getPersonName(contact, _),
    customerNumber ? `#${customerNumber}` : undefined,
  ]
    .filter((t) => !!t)
    .join(' ');
}

function formatPersonOption(
  contact: FindCustomersQuery['persons']['data'][number],
  _: any,
) {
  return (
    <>
      <div className={styles.itemRow1}>{getPersonLabel(contact, _)}</div>
      <div className={styles.itemRow2}>
        {[
          contact.user?.username,
          contact.email ? `(${contact.email})` : undefined,
        ]
          .filter((t) => !!t)
          .join(' ')}
      </div>
      {(contact.address?.streetAndNumber || contact.address?.city) && (
        <div className={styles.itemRow3}>
          {[
            contact.address.streetAndNumber,
            [contact.address.postalCode, contact.address.city]
              .filter((t) => !!t)
              .join(' '),
          ]
            .filter((t) => !!t)
            .join(', ')}
        </div>
      )}
    </>
  );
}

export type CustomerItemType = {
  label: string;
  value: string;
  customerId: string;
  customerNumber?: string | null;
  username?: string | null;
  status?: Types.UserStatus | null;
  gender?: Types.Gender | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  address?: {
    streetAndNumber?: string | null;
    postalCode?: string | null;
    city?: string | null;
  } | null;
  invoiceAddress?: {
    company?: string | null;
    streetAndNumber?: string | null;
    postalCode?: string | null;
    city?: string | null;
  } | null;
  invoiceEmail?: string | null;
};
interface CustomerFinderProps {
  value?: CustomerItemType | null;
  onChange: (item?: CustomerItemType | null) => void;
}

export const CustomerFinder: ComponentType<CustomerFinderProps> = (props) => {
  const { value, onChange } = props;
  const _ = Translator.useTranslator();

  const [__, { data, refetch }] = useFindCustomersLazyQuery();
  return (
    <div className={styles.host}>
      <label>Suche</label>
      <AsyncSelect<CustomerItemType>
        onChange={onChange}
        value={value}
        formatOptionLabel={(item, { context }) =>
          context === 'menu'
            ? (item as any).__contact
              ? formatPersonOption((item as any).__contact, _)
              : item.label
            : item.label
        }
        loadOptions={async (inputValue: string, callback) => {
          try {
            const data = await refetch({
              query: inputValue,
            });
            callback(
              data.data?.persons.data.map((person) => ({
                label: getPersonLabel(person, _),
                value: person.id,
                customerId: person.user?.id,
                customerNumber: (person.user as any)?.customerNumber ?? null,
                username: person.user?.username ?? null,
                status: person.user?.status ?? null,
                gender: person.gender ?? null,
                firstName: person.firstName ?? null,
                lastName: person.lastName ?? null,
                email: person.email ?? null,
                address: person.address ?? null,
                invoiceAddress: person.invoiceAddress ?? null,
                invoiceEmail: (person.user as any)?.invoiceEmail ?? null,
                __contact: person,
              })) as any,
            );
          } catch (e) {
            console.log(e);
          }
        }}
      />
    </div>
  );
};
